import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  RefreshCw, 
  Plus, 
  DollarSign, 
  Calendar,
  TrendingUp,
  Users,
  Pencil,
  Trash2,
  CheckCircle,
  PauseCircle,
  PlayCircle,
  BarChart3,
  Wallet
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' }
];

const CATEGORY_OPTIONS = [
  'Retainer',
  'Consulting',
  'Subscription',
  'Contract',
  'Royalties',
  'Commission',
  'Rental Income',
  'Other'
];

function RecurringIncome() {
  const [loading, setLoading] = useState(true);
  const [recurringIncome, setRecurringIncome] = useState([]);
  const [summary, setSummary] = useState({});
  const [forecast, setForecast] = useState([]);
  const [clients, setClients] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [selectedIncome, setSelectedIncome] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    client_id: '',
    client_name: '',
    amount: '',
    frequency: 'monthly',
    category: 'Retainer',
    start_date: '',
    end_date: '',
    next_expected: '',
    notes: ''
  });
  const [recordData, setRecordData] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [incomeRes, forecastRes, clientsRes] = await Promise.all([
        axios.get(`${API_URL}/api/business/recurring-income`, { withCredentials: true }),
        axios.get(`${API_URL}/api/business/revenue-forecast`, { 
          params: { months: 3 },
          withCredentials: true 
        }),
        axios.get(`${API_URL}/api/business/clients`, { withCredentials: true }).catch(() => ({ data: { clients: [] } }))
      ]);
      
      setRecurringIncome(incomeRes.data.recurring_income || []);
      setSummary(incomeRes.data.summary || {});
      setForecast(forecastRes.data.forecast || []);
      setClients(clientsRes.data.clients || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load recurring income data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddOrEdit = async () => {
    if (!formData.name || !formData.amount) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      if (selectedIncome) {
        await axios.put(
          `${API_URL}/api/business/recurring-income/${selectedIncome.id}`,
          formData,
          { withCredentials: true }
        );
        toast.success('Recurring income updated');
      } else {
        await axios.post(
          `${API_URL}/api/business/recurring-income`,
          formData,
          { withCredentials: true }
        );
        toast.success('Recurring income added');
      }
      
      setShowAddModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save recurring income');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this recurring income?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/business/recurring-income/${id}`, { withCredentials: true });
      toast.success('Recurring income deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleToggleStatus = async (income) => {
    const newStatus = income.status === 'active' ? 'paused' : 'active';
    
    try {
      await axios.put(
        `${API_URL}/api/business/recurring-income/${income.id}`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success(`Recurring income ${newStatus === 'active' ? 'activated' : 'paused'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleRecordPayment = async () => {
    if (!selectedIncome) return;
    
    try {
      await axios.post(
        `${API_URL}/api/business/recurring-income/${selectedIncome.id}/record-payment`,
        recordData,
        { withCredentials: true }
      );
      toast.success('Payment recorded successfully');
      setShowRecordModal(false);
      setRecordData({
        amount: '',
        date: new Date().toISOString().split('T')[0],
        notes: ''
      });
      setSelectedIncome(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  const openEditModal = (income) => {
    setSelectedIncome(income);
    setFormData({
      name: income.name || '',
      client_id: income.client_id || '',
      client_name: income.client_name || '',
      amount: income.amount || '',
      frequency: income.frequency || 'monthly',
      category: income.category || 'Retainer',
      start_date: income.start_date || '',
      end_date: income.end_date || '',
      next_expected: income.next_expected || '',
      notes: income.notes || ''
    });
    setShowAddModal(true);
  };

  const openRecordModal = (income) => {
    setSelectedIncome(income);
    setRecordData({
      amount: income.amount || '',
      date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setShowRecordModal(true);
  };

  const resetForm = () => {
    setSelectedIncome(null);
    setFormData({
      name: '',
      client_id: '',
      client_name: '',
      amount: '',
      frequency: 'monthly',
      category: 'Retainer',
      start_date: '',
      end_date: '',
      next_expected: '',
      notes: ''
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getFrequencyLabel = (freq) => {
    return FREQUENCY_OPTIONS.find(f => f.value === freq)?.label || freq;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse text-muted-foreground">Loading recurring income...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="recurring-income-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" data-testid="ri-title">Recurring Income</h1>
          <p className="text-muted-foreground mt-1">
            Track retainers, subscriptions, and recurring revenue
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={fetchData} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={() => setShowAddModal(true)} data-testid="add-recurring-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Recurring Income
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-emerald-500/20 bg-emerald-500/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Monthly Recurring</p>
                <p className="text-2xl font-bold text-emerald-400" data-testid="monthly-total">
                  {formatCurrency(summary.monthly_recurring)}
                </p>
              </div>
              <div className="p-3 rounded-full bg-emerald-500/10">
                <RefreshCw className="w-6 h-6 text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-blue-500/20 bg-blue-500/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Annual Projected</p>
                <p className="text-2xl font-bold text-blue-400" data-testid="annual-total">
                  {formatCurrency(summary.annual_projected)}
                </p>
              </div>
              <div className="p-3 rounded-full bg-blue-500/10">
                <TrendingUp className="w-6 h-6 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-purple-500/20 bg-purple-500/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Sources</p>
                <p className="text-2xl font-bold text-purple-400" data-testid="active-count">
                  {summary.active_sources || 0}
                </p>
              </div>
              <div className="p-3 rounded-full bg-purple-500/10">
                <Users className="w-6 h-6 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-amber-500/20 bg-amber-500/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Sources</p>
                <p className="text-2xl font-bold text-amber-400" data-testid="total-count">
                  {summary.total_sources || 0}
                </p>
              </div>
              <div className="p-3 rounded-full bg-amber-500/10">
                <Wallet className="w-6 h-6 text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Forecast */}
      {forecast.length > 0 && (
        <Card data-testid="forecast-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Revenue Forecast
            </CardTitle>
            <CardDescription>Projected income for the next 3 months</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {forecast.map((month, idx) => (
                <div 
                  key={idx} 
                  className="p-4 rounded-lg bg-muted/30 border border-border"
                >
                  <p className="text-sm text-muted-foreground">{month.month_label}</p>
                  <p className="text-xl font-bold text-emerald-400">
                    {formatCurrency(month.projected_income)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    From {month.sources_count} source{month.sources_count !== 1 ? 's' : ''}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recurring Income List */}
      <Card data-testid="income-list-card">
        <CardHeader>
          <CardTitle>Income Sources</CardTitle>
          <CardDescription>Manage your recurring income streams</CardDescription>
        </CardHeader>
        <CardContent>
          {recurringIncome.length > 0 ? (
            <div className="space-y-4">
              {recurringIncome.map((income) => (
                <div 
                  key={income.id}
                  className="p-4 rounded-lg border border-border bg-card hover:bg-muted/30 transition-colors"
                  data-testid={`income-item-${income.id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold">{income.name}</h3>
                        <Badge variant={income.status === 'active' ? 'default' : 'secondary'}>
                          {income.status === 'active' ? (
                            <CheckCircle className="w-3 h-3 mr-1" />
                          ) : (
                            <PauseCircle className="w-3 h-3 mr-1" />
                          )}
                          {income.status}
                        </Badge>
                        <Badge variant="outline">{getFrequencyLabel(income.frequency)}</Badge>
                      </div>
                      {income.client_name && (
                        <p className="text-sm text-muted-foreground mt-1">
                          Client: {income.client_name}
                        </p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          {formatCurrency(income.amount)} / {income.frequency}
                        </span>
                        {income.next_expected && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            Next: {income.next_expected}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openRecordModal(income)}
                        className="text-emerald-400 hover:text-emerald-300"
                        data-testid={`record-payment-${income.id}`}
                      >
                        <DollarSign className="w-4 h-4 mr-1" />
                        Record Payment
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleToggleStatus(income)}
                        data-testid={`toggle-status-${income.id}`}
                      >
                        {income.status === 'active' ? (
                          <PauseCircle className="w-4 h-4 text-amber-400" />
                        ) : (
                          <PlayCircle className="w-4 h-4 text-emerald-400" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditModal(income)}
                        data-testid={`edit-${income.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(income.id)}
                        className="text-red-400 hover:text-red-300"
                        data-testid={`delete-${income.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <RefreshCw className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-medium mb-2">No recurring income yet</h3>
              <p className="text-sm mb-4">
                Add your retainers, subscriptions, and other recurring revenue
              </p>
              <Button onClick={() => setShowAddModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Source
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={showAddModal} onOpenChange={(open) => {
        setShowAddModal(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {selectedIncome ? 'Edit Recurring Income' : 'Add Recurring Income'}
            </DialogTitle>
            <DialogDescription>
              Track retainers, subscriptions, and other recurring revenue streams
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., Monthly Retainer - Acme Corp"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="input-name"
                />
              </div>
              
              <div>
                <Label htmlFor="amount">Amount *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="amount"
                    type="number"
                    className="pl-9"
                    placeholder="0.00"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || '' })}
                    data-testid="input-amount"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="frequency">Frequency</Label>
                <Select 
                  value={formData.frequency}
                  onValueChange={(val) => setFormData({ ...formData, frequency: val })}
                >
                  <SelectTrigger data-testid="select-frequency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCY_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="category">Category</Label>
                <Select 
                  value={formData.category}
                  onValueChange={(val) => setFormData({ ...formData, category: val })}
                >
                  <SelectTrigger data-testid="select-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="client">Client (Optional)</Label>
                <Select 
                  value={formData.client_id}
                  onValueChange={(val) => {
                    const client = clients.find(c => c.id === val);
                    setFormData({ 
                      ...formData, 
                      client_id: val,
                      client_name: client?.name || ''
                    });
                  }}
                >
                  <SelectTrigger data-testid="select-client">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">No client</SelectItem>
                    {clients.map(client => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="start_date">Start Date</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  data-testid="input-start-date"
                />
              </div>
              
              <div>
                <Label htmlFor="next_expected">Next Expected</Label>
                <Input
                  id="next_expected"
                  type="date"
                  value={formData.next_expected}
                  onChange={(e) => setFormData({ ...formData, next_expected: e.target.value })}
                  data-testid="input-next-expected"
                />
              </div>
              
              <div className="col-span-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  placeholder="Any additional details..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  data-testid="input-notes"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddOrEdit} data-testid="save-btn">
              {selectedIncome ? 'Update' : 'Add'} Recurring Income
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Payment Modal */}
      <Dialog open={showRecordModal} onOpenChange={setShowRecordModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              Record a payment received for: {selectedIncome?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="record_amount">Amount</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="record_amount"
                  type="number"
                  className="pl-9"
                  value={recordData.amount}
                  onChange={(e) => setRecordData({ ...recordData, amount: parseFloat(e.target.value) || '' })}
                  data-testid="record-amount"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="record_date">Date</Label>
              <Input
                id="record_date"
                type="date"
                value={recordData.date}
                onChange={(e) => setRecordData({ ...recordData, date: e.target.value })}
                data-testid="record-date"
              />
            </div>
            
            <div>
              <Label htmlFor="record_notes">Notes (Optional)</Label>
              <Input
                id="record_notes"
                placeholder="Payment reference, invoice #, etc."
                value={recordData.notes}
                onChange={(e) => setRecordData({ ...recordData, notes: e.target.value })}
                data-testid="record-notes"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecordModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleRecordPayment} data-testid="confirm-record-btn">
              <CheckCircle className="w-4 h-4 mr-2" />
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default RecurringIncome;
